import type { RetryCompanyDomainData } from 'api/dist/routes/organization/[organization_id]/company-domain/index.js'
import type { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import { GetCompanyDomainQuery } from './get-company-domain-query'

export interface IRetryCompanyDomainParams {
    organizationId: string
    domain: string
}

export type IRetryCompanyDomainResult = RetryCompanyDomainData

/** Requests one server-listed domain; the API independently revalidates its Clerk evidence. */
export class RetryCompanyDomainMutation {
    static call(api: IApi, params: IRetryCompanyDomainParams) {
        return api
            .post<
                RetryCompanyDomainData,
                { domain: string }
            >(`/organization/${params.organizationId}/company-domain/retry`, { domain: params.domain })
            .then((response) => response.data)
    }
}

/** Keeps cache invalidation bound to the workspace supplied to this exact mutation attempt. */
export function getRetryCompanyDomainInvalidation(params: IRetryCompanyDomainParams) {
    return {
        queryKey: GetCompanyDomainQuery.getKey({ organizationId: params.organizationId }),
        refetchType: 'none' as const,
    }
}

/** Requests one selected candidate and marks authoritative Clerk status stale for an explicit readback. */
export function useRetryCompanyDomainMutation() {
    const { api, qc } = useApiToolbox()

    return useApiMutation<IRetryCompanyDomainResult, IRetryCompanyDomainParams>({
        mutationFn: (params) => RetryCompanyDomainMutation.call(api, params),
        onSuccess: (_result, params) => qc.invalidateQueries(getRetryCompanyDomainInvalidation(params)),
    })
}
